import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Modal } from "./ui-bits";
import { Truck } from "lucide-react";

const n = (v) => Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });

export function ReceiveGoodsButton({ onDone, className = "btn-gold inline-flex items-center gap-2" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className} data-testid="receive-goods-btn">
        <Truck size={14}/> Registra arrivo merce
      </button>
      {open && <ReceiveGoodsModal onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone?.(); }}/>}
    </>
  );
}

function ReceiveGoodsModal({ onClose, onDone }) {
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [rows, setRows] = useState(null);
  const [qty, setQty] = useState({});
  const [cost, setCost] = useState({});
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [pendingPOs, setPendingPOs] = useState([]);
  const [closePOs, setClosePOs] = useState({});

  useEffect(() => { api.get("/suppliers").then(r => setSuppliers(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setPendingPOs([]); setClosePOs({});
    if (!supplierId) return;
    let alive = true;
    api.get("/purchase-orders", { params: { status: "In attesa", supplier_id: supplierId } }).then(r => {
      if (!alive) return;
      setPendingPOs(r.data);
      setClosePOs(Object.fromEntries(r.data.map(p => [p.id, true])));
    }).catch(() => {});
    return () => { alive = false; };
  }, [supplierId]);
  useEffect(() => {
    let alive = true;
    setRows(null); setErr("");
    api.get("/inventory/receipt-suggestions", { params: { supplier_id: supplierId || undefined } }).then(r => {
      if (!alive) return;
      setRows(r.data);
      setQty(Object.fromEntries(r.data.map(x => [x.material_id, supplierId && (x.incoming || x.suggested) > 0 ? String(x.incoming || x.suggested) : ""])));
      setCost(Object.fromEntries(r.data.map(x => [x.material_id, x.cost_per_unit ? String(x.cost_per_unit) : ""])));
    }).catch(() => { if (alive) { setRows([]); setErr("Impossibile caricare i materiali. Riprova."); } });
    return () => { alive = false; };
  }, [supplierId]);

  const lines = (rows || []).filter(r => Number(qty[r.material_id]) > 0).map(r => {
    const c = Number(cost[r.material_id]);
    return { material_id: r.material_id, quantity: Number(qty[r.material_id]), unit_cost: c > 0 && c !== Number(r.cost_per_unit) ? c : null };
  });
  const total = (rows || []).reduce((s, r) => s + (Number(qty[r.material_id]) || 0) * (Number(cost[r.material_id]) || 0), 0);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/inventory/receipts", { supplier_id: supplierId || null, reference: reference || null, notes: notes || null, lines,
        close_po_ids: Object.keys(closePOs).filter(k => closePOs[k]) });
      onDone();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Modal open onClose={onClose} title="Arrivo merce" wide>
      <div data-testid="receive-goods-modal">
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div><label className="label-caps block mb-2">Fornitore</label>
            <select className="input-dark" value={supplierId} onChange={e => setSupplierId(e.target.value)} data-testid="receive-supplier">
              <option value="">Tutti i materiali</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select></div>
          <div><label className="label-caps block mb-2">Rif. documento (DDT / fattura)</label>
            <input className="input-dark" value={reference} onChange={e => setReference(e.target.value)} placeholder="es. DDT 123/2026" data-testid="receive-reference"/></div>
        </div>
        {supplierId && <p className="text-xs opacity-60 mb-3">Quantità precompilate con la merce ordinata in attesa (o, se non c'è, con quanto suggerito). Modificale con quanto è arrivato davvero.</p>}
        {!rows ? <div className="text-sm opacity-60">Caricamento…</div> : rows.length === 0 ?
          <div className="text-sm opacity-60" data-testid="receive-empty">Nessun materiale per questo fornitore.</div> : (
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="tbl w-full text-xs [&_td]:!px-2 [&_th]:!px-2">
              <thead><tr><th className="text-left">Materiale</th><th className="text-right">In magazzino</th><th className="text-right">Suggerito</th><th className="text-right">Arrivato</th><th className="text-right">Prezzo €/u</th></tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.material_id} data-testid={`receive-row-${r.material_id}`}>
                    <td><div className="font-medium">{r.name}</div><div className="opacity-60">{[r.code, r.color].filter(Boolean).join(" · ")}</div></td>
                    <td className="text-right whitespace-nowrap">{n(r.stock)} {r.unit}</td>
                    <td className="text-right whitespace-nowrap">{r.incoming > 0 ? `${n(r.incoming)} ${r.unit} in arrivo` : r.suggested > 0 ? `${n(r.suggested)} ${r.unit}` : "—"}</td>
                    <td className="text-right"><input type="number" min="0" step="0.01" className="input-dark w-24 text-right py-1" value={qty[r.material_id] ?? ""}
                      onChange={e => setQty({ ...qty, [r.material_id]: e.target.value })} data-testid={`receive-qty-${r.material_id}`}/></td>
                    <td className="text-right"><input type="number" min="0" step="0.01" className="input-dark w-24 text-right py-1" value={cost[r.material_id] ?? ""}
                      onChange={e => setCost({ ...cost, [r.material_id]: e.target.value })} data-testid={`receive-cost-${r.material_id}`}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-4"><label className="label-caps block mb-2">Note</label>
          <input className="input-dark" value={notes} onChange={e => setNotes(e.target.value)} data-testid="receive-notes"/></div>
        <p className="text-xs opacity-60 mt-3">Se cambi il prezzo, il costo del materiale viene aggiornato.</p>
        {pendingPOs.length > 0 && (
          <div className="mt-4 p-4 border text-sm" style={{ borderColor: "var(--border)" }} data-testid="receive-close-pos">
            <div className="label-caps mb-2">Ordini al fornitore da segnare come ricevuti</div>
            {pendingPOs.map(p => (
              <label key={p.id} className="flex items-center gap-2 py-1 cursor-pointer">
                <input type="checkbox" checked={!!closePOs[p.id]} onChange={e => setClosePOs({ ...closePOs, [p.id]: e.target.checked })} data-testid={`receive-close-po-${p.id}`}/>
                <span><b>{p.number}</b> · {p.lines.map(l => `${l.name} ${n(l.quantity)} ${l.unit}`).join(", ")}</span>
              </label>
            ))}
            <div className="text-xs opacity-60 mt-1">Togli la spunta se è arrivata solo una parte: l'ordine resta in attesa.</div>
          </div>
        )}
        {err && <div className="text-red-600 text-sm mt-3" data-testid="receive-error">{err}</div>}
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm" data-testid="receive-summary">{lines.length} materiali · € {total.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-ghost">Annulla</button>
            <button type="button" disabled={!lines.length || busy} onClick={save} className="btn-gold" data-testid="receive-save">{busy ? "Salvataggio…" : "Carica in magazzino"}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function LowStockCard({ refreshKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/inventory/low-stock").then(r => setItems(r.data)).catch(() => {}); }, [refreshKey]);
  if (!items.length) return null;
  return (
    <div className="card-dark p-6 mb-8 border-l-2 border-l-red-600" data-testid="low-stock-card">
      <div className="label-caps mb-3 text-red-700">Sotto scorta minima ({items.length})</div>
      <ul className="text-sm space-y-2">
        {items.map(m => (
          <li key={m.id} className="flex flex-wrap justify-between gap-2 border-b last:border-0 pb-2" style={{ borderColor: "var(--border)" }} data-testid={`low-stock-${m.id}`}>
            <span>{m.name} <span className="text-xs opacity-60">{m.code || ""}</span></span>
            <span className="text-xs">Libero <b>{n(m.free)}</b> {m.unit} · minimo {n(m.min_stock)} · riservato {n(m.reserved)}{m.incoming > 0 ? ` · in arrivo ${n(m.incoming)}` : ""}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
